import { PersonaEditor } from '@/components/admin/personas/persona-editor';
import { PmsSpinner } from '@/components/ui/pms-spinner';
import { useAdminSession, useRejectOn401 } from '@/lib/admin-session';
import { ApiError, fetchPersona, fetchPersonaVersion } from '@/lib/api';
import { readCopyFlag, useConsumedCopyFlag } from '@/lib/copy-search';
import { useQuery } from '@tanstack/react-query';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';

/**
 * L'éditeur d'un persona existant (refonte modulaire §4, §10; maquettes
 * `EditeurNomme`, `EditeurAnonyme`).
 *
 * The criterion's route file states the four rules this one follows, and none
 * of them differs here - they belong to the versioning pattern, not to the
 * object:
 *
 *   - **two queries, two shapes, two key prefixes** (issue #16):
 *     `['persona', email, id]` holds a `PersonaDetail`, `['persona-version',
 *     email, id, versionId]` holds a `PersonaVersion`, and neither overlaps
 *     `['personas', email]`, which the list fills with the card projection;
 *   - **« Voir » travels in the URL** as `?version=<id>`, because a version
 *     somebody is reading is exactly what gets pasted into a thread, and
 *     `validateSearch` never throws;
 *   - **the remount key is the id plus a COUNTER**, never the draft's stamp:
 *     every autosave answers a new stamp into this very query, so a key built
 *     on it would change once a second while somebody types and remount the
 *     textarea under their caret;
 *   - **the bump waits for the refetch to LAND**, because remounting before the
 *     fresh answer arrives would re-seed the editor from the stale draft it is
 *     escaping.
 */

type PersonaSearch = { version?: string; copie?: true };

function readSearch(search: Record<string, unknown>): PersonaSearch {
  const raw = search.version;
  const parsed: PersonaSearch = typeof raw === 'string' && raw !== '' ? { version: raw } : {};
  const copie = readCopyFlag(search.copie);
  // OMITTED and never `undefined`: TanStack writes every key it is handed into
  // the address bar, so a present key would put `?copie=` on every editor.
  if (copie !== undefined) parsed.copie = copie;
  return parsed;
}

export const Route = createFileRoute('/admin/personas/$personaId')({
  validateSearch: readSearch,
  component: PersonaEditorPage,
});

function PersonaEditorPage() {
  const { email } = useAdminSession();
  const { personaId } = Route.useParams();
  const { version, copie } = Route.useSearch();
  const navigate = useNavigate();

  /** Bumped by « Recharger le brouillon du serveur », and by nothing else. */
  const [reload, setReload] = useState(0);

  const persona = useQuery({
    queryKey: ['persona', email, personaId],
    queryFn: () => fetchPersona(personaId),
    retry: false,
  });

  const viewed = useQuery({
    queryKey: ['persona-version', email, personaId, version ?? null],
    queryFn: () => fetchPersonaVersion(personaId, version ?? ''),
    enabled: version !== undefined,
    retry: false,
  });

  /*
    The flag is CONSUMED once the editor it describes has OPENED (§4.2).

    Two halves, and both matter. Left in the address bar it would re-select
    the name field at every later remount - and the counter just above is
    one: « Recharger le brouillon du serveur » bumps it. Dropped too EARLY it
    would never select anything at all: the editor reads the flag on ITS
    mount, and it does not mount until the entity has been read - so this
    waits for the entity, and React runs a child's effects before its
    parent's, which is what puts the selection before the drop.

    `replace`, because it is a hand-off between two screens and not a place
    anybody goes back to.
  */
  useConsumedCopyFlag(copie, persona.data !== undefined, () =>
    navigate({
      to: '/admin/personas/$personaId',
      params: { personaId },
      // Rebuilt from what THIS route holds rather than reduced from the
      // previous search: TanStack types that argument as the union of every
      // route's shape, and a key OMITTED is the only spelling
      // `exactOptionalPropertyTypes` accepts.
      search: version === undefined ? {} : { version },
      replace: true,
    }),
  );

  const rejected = useRejectOn401(persona.error);
  const missing = persona.error instanceof ApiError && persona.error.status === 404;

  return (
    <div className="space-y-6">
      <p className="text-gray-600 text-sm">
        <Link to="/admin/personas" className="text-bleu-nuit underline underline-offset-2">
          Personas
        </Link>
        <span className="mx-2">›</span>
        {persona.data?.name ?? '...'}
      </p>

      {persona.isPending ? (
        <div className="flex justify-center py-12">
          <PmsSpinner label="Chargement du persona" />
        </div>
      ) : null}

      {missing ? (
        <p className="text-gray-700">
          Ce persona n’existe pas.{' '}
          <Link to="/admin/personas" className="text-bleu-nuit underline underline-offset-2">
            Revenir à la bibliothèque
          </Link>
          .
        </p>
      ) : null}

      {persona.isError && !missing && !rejected ? (
        <p className="text-gray-700">Ce persona n’a pas pu être chargé.</p>
      ) : null}

      {persona.data === undefined ? null : (
        <PersonaEditor
          key={`${persona.data.id}-${reload}`}
          selectName={copie === true}
          detail={persona.data}
          viewing={viewed.data ?? null}
          onView={(summary) =>
            navigate({
              to: '/admin/personas/$personaId',
              params: { personaId },
              search: { version: summary.id },
            })
          }
          onCloseView={() =>
            navigate({
              to: '/admin/personas/$personaId',
              params: { personaId },
              search: {},
            })
          }
          onReload={() => {
            void persona.refetch().then(() => setReload((was) => was + 1));
          }}
        />
      )}
    </div>
  );
}
